import React from "react";
import { Typography, Chip, Box, MenuItem, ListItemIcon } from "@mui/material";
import DetallePedido from "../../pedidosEnvasado/DetallePedido";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { getColorEstado } from "../../pedidosEnvasado/VistaPedidosSimple/config/estadosPedido";
import { ORIGEN_CONFIG, ORIGENES } from "../constants";

export const createColumns = () => [
  {
    accessorKey: "id",
    header: "N° Pedido",
    size: 90,
    Cell: ({ cell }) => (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
        {cell.getValue()}
      </Typography>
    ),
  },
  {
    accessorKey: "cliente",
    header: "Cliente",
    size: 200,
    Cell: ({ cell }) => (
      <Typography variant="body2">
        {cell.getValue()}
      </Typography>
    ),
  },
  {
    accessorKey: "direccion_entrega",
    header: "Dirección",
    size: 220,
    Cell: ({ cell }) => (
      <Typography variant="body2" color="text.secondary" noWrap>
        {cell.getValue()}
      </Typography>
    ),
  },
  {
    accessorKey: "valor",
    header: "Estado",
    size: 130,
    enableGrouping: true,
    Cell: ({ cell, row }) => (
      <Chip
        label={cell.getValue() || "—"}
        color={getColorEstado(row.original.estado).chip}
        size="small"
        variant="outlined"
        sx={{ fontWeight: 400 }}
      />
    ),
  },
  {
    accessorKey: "origen",
    header: "Origen",
    size: 120,
    enableGrouping: true,
    Cell: ({ cell }) => {
      const origen = cell.getValue() || ORIGENES.SIN_ORIGEN;
      const config = ORIGEN_CONFIG[origen] || ORIGEN_CONFIG[ORIGENES.SIN_ORIGEN];
      return (
        <Chip
          label={origen}
          color={config.color}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 400 }}
        />
      );
    },
  },
  {
    accessorKey: "nombre_apellido",
    header: "Chofer",
    size: 130,
    Cell: ({ cell }) => (
      <Typography variant="body2" color="text.secondary">
        {cell.getValue() || "—"}
      </Typography>
    ),
  },
  {
    accessorKey: "sucursal_cliente",
    header: "Sucursal",
    size: 130,
    enableGrouping: true,
    Cell: ({ cell }) => (
      <Typography variant="body2">
        {cell.getValue()}
      </Typography>
    ),
  },
  {
    accessorKey: "fecha_format",
    header: "Fecha",
    size: 110,
    enableGrouping: true,
    Cell: ({ cell }) => (
      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
        {cell.getValue()}
      </Typography>
    ),
  },
];

export const createTableConfig = (columns, pedidos, handlers, TABLE_INITIAL_STATE) => ({
  columns,
  data: pedidos,
  layoutMode: "grid",
  enableColumnActions: false,
  enableGlobalFilter: true,
  enableHiding: false,
  enableRowActions: true,
  enableGrouping: true,
  enablePinning: true,
  enableColumnFilters: false,
  enableFullScreenToggle: false,
  enableDensityToggle: false,
  enableExpandAll: false,
  positionActionsColumn: "last",
  positionToolbarAlertBanner: "none",
  localization: {
    actions: "Acciones",
    noRecordsToDisplay: "No hay registros para mostrar",
    search: "Buscar",
    clearSearch: "Limpiar búsqueda",
    showAll: "Mostrar todos",
    showMore: "Mostrar más",
    showLess: "Mostrar menos",
    filter: "Filtrar",
    clearFilter: "Limpiar filtro",
    filterByColumn: "Filtrar por {column}",
    groupByColumn: "Agrupar por {column}",
    ungroupByColumn: "Desagrupar por {column}",
    addFilter: "Agregar filtro",
    removeFilter: "Remover filtro",
    addGroup: "Agregar grupo",
    removeGroup: "Remover grupo",
    pinToLeft: "Fijar a la izquierda",
    pinToRight: "Fijar a la derecha",
    unpin: "Desfijar",
    sortByColumnAsc: "Ordenar {column} ascendente",
    sortByColumnDesc: "Ordenar {column} descendente",
    hideColumn: "Ocultar columna",
    showColumn: "Mostrar columna",
    toggleSelectAll: "Seleccionar todos",
    toggleSelectRow: "Seleccionar fila",
    selectedCount: "{count} fila(s) seleccionada(s)",
    rowsPerPage: "Filas por página",
    of: "de",
    page: "Página",
    firstPage: "Primera página",
    lastPage: "Última página",
    previousPage: "Página anterior",
    nextPage: "Página siguiente",
  },
  initialState: TABLE_INITIAL_STATE,
  muiTableHeadCellProps: {
    align: "left",
    sx: {
      fontSize: 12,
      color: "#5f6368",
      fontWeight: 600,
      backgroundColor: "#f8f9fa",
      borderBottom: "1px solid #e0e0e0",
      py: 1.5,
      letterSpacing: 0.5,
      textTransform: "uppercase",
    },
  },
  muiTableBodyRowProps: () => ({
    sx: {
      cursor: "pointer",
      backgroundColor: "white",
      transition: "background-color 0.15s ease",
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  }),
  muiSearchTextFieldProps: {
    placeholder: "Buscar...",
    sx: {
      minWidth: "240px",
      backgroundColor: "#fff",
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
      }
    },
    variant: "outlined",
    size: "small",
  },
  renderDetailPanel: ({ row }) => (
    <Box sx={{ bgcolor: "#fafafa", p: 2 }}>
      <DetallePedido row={row.original} />
    </Box>
  ),
  renderTopToolbarCustomActions: () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <ListAltIcon sx={{ fontSize: 20, color: "#5f6368" }} />
      <Typography variant="subtitle1" fontWeight={500} sx={{ color: "#202124" }}>
        Todos los Pedidos
      </Typography>
      <Typography variant="body2" sx={{ color: "#5f6368" }}>
        ({pedidos.length})
      </Typography>
    </Box>
  ),
  renderRowActionMenuItems: ({ closeMenu, row }) => {
    const items = [
      {
        key: "ver",
        label: "Ver Factura",
        icon: <PictureAsPdfIcon fontSize="small" />,
        onClick: () => handlers.handleVerFactura(row.original),
        visible: !!row.original.id_factura,
      },
      {
        key: "verNC",
        label: "Ver Nota de Crédito",
        icon: <VisibilityOffIcon fontSize="small" />,
        onClick: () => handlers.handleVerNC(row.original),
        visible: !!row.original.id_nc,
      },
    ].filter(item => item.visible);

    if (items.length === 0) {
      return [
        <MenuItem key="sinAcciones" disabled>
          Sin comprobantes
        </MenuItem>
      ];
    }

    return items.map(({ key, label, icon, onClick }) => (
      <MenuItem key={key} onClick={() => { closeMenu(); onClick(); }}>
        <ListItemIcon>{icon}</ListItemIcon>
        {label}
      </MenuItem>
    ));
  },
  muiTablePaperProps: {
    elevation: 0,
    sx: {
      borderRadius: 1,
      border: "1px solid #e0e0e0",
      overflow: "hidden",
    },
  },
});
